import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FamilyInfoModal } from './FamilyInfoModal';
import { HoverTooltip } from '../lib/HoverTooltip';
import { useAuth } from '../lib/auth';

type Family = 'precast' | 'curbthroat' | 'castiron' | 'nyloplast';

interface FamilyTile {
  family: Family;
  title: string;
  image: string;
  permanentOnly?: boolean;
}

const TILES: FamilyTile[] = [
  {
    family: 'precast',
    title: 'Precast Concrete Structures with\u00a0Grates',
    image: '/images/PRODUCT/Precast-4Seat.png',
  },
  {
    family: 'curbthroat',
    title: 'Open Throat Curb\u00a0Inlets',
    image: '/images/PRODUCT/Product-OpenThroat.png',
    permanentOnly: true,
  },
  {
    family: 'castiron',
    title: 'Cast Iron Frame and\u00a0Grate',
    image: '/images/PRODUCT/Product-CastIron.png',
  },
  {
    family: 'nyloplast',
    title: 'Nyloplast Castings',
    image: '/images/PRODUCT/Product-Nyloplast.png',
  },
];

/**
 * Step 2 — drainage structure family selection. Markup mirrors the legacy
 * `resources/jsx/components/page2.jsx` (Bootstrap 5 + Hyper theme).
 */
export function StructureSelectionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [infoFamily, setInfoFamily] = useState<Family | null>(null);

  const tempParam = params.get('temp');
  const oilParam = params.get('oil');
  const modeParam = params.get('mode');
  const editId = params.get('id')?.trim() || null;

  if (!tempParam || !modeParam) {
    return <Navigate to="/newstructure" replace />;
  }

  const isPermanent = tempParam === 'false';
  const isAdsEmployee = user?.customerType?.trim().toLowerCase() === 'ads employee';
  const visibleTiles = TILES.filter((t) => !t.permanentOnly || isPermanent);

  const choose = (tile: FamilyTile) => {
    const idParam = editId ? `&id=${editId}` : '';
    const qs = `family=${tile.family}&temp=${tempParam}&oil=${oilParam ?? 'false'}${idParam}`;
    navigate(modeParam === 'casting' ? `/newstructure/casting?${qs}` : `/newstructure/dimensions?${qs}`);
  };

  return (
    <div className="container-fluid">
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <h1>Drainage Structure Selection</h1>
              </div>

              {visibleTiles.map((tile) => (
                <div
                  key={tile.family}
                  className="col-12 col-md-6 col-xl-3 p-2 d-grid text-center"
                >
                  <a
                    className="text-reset"
                    style={{ cursor: 'pointer' }}
                    onClick={() => choose(tile)}
                  >
                    <img
                      className="border mx-auto img-fluid"
                      src={tile.image}
                      alt={tile.title}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="d-flex align-items-center" style={{ height: '60px' }}>
                      <span className="fs-2 flex-fill text-center">{tile.title}</span>
                    </div>
                  </a>

                  <HoverTooltip
                    text={`Click here to view ${tile.title.replace(/\u00a0/g, ' ')} photos and dimensional drawings as well as examples of FlexStorm installed in this drainage structure.`}
                    maxWidth={260}
                  >
                    <span
                      style={{ cursor: 'pointer', display: 'block' }}
                      onClick={() => setInfoFamily(tile.family)}
                    >
                      <a className="btn btn-outline-light p-0">
                        <div className="d-flex flex-row pb-2">
                          <div className="w-100 text-start ps-1">
                            <img
                              className="p-1"
                              src="/images/ICON/Icon_Image_Small.png"
                              alt=""
                              style={{ display: 'inline-block', verticalAlign: 'middle' }}
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <img
                              className="p-1"
                              src="/images/ICON/Icon_Transform_Small.png"
                              alt=""
                              style={{ display: 'inline-block', verticalAlign: 'middle' }}
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <br />
                            <span className="p-1">More Information</span>
                          </div>
                          <div className="flex-shrink-1 text-end">
                            <img
                              className="p-2"
                              src="/images/ICON/Icon_Info_Small.png"
                              alt=""
                              style={{ display: 'inline-block', verticalAlign: 'middle' }}
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      </a>
                    </span>
                  </HoverTooltip>
                </div>
              ))}

              <div className="col-12 text-center">
                <Link
                  to="/newstructure"
                  className="btn btn-sm mt-2 py-1 btn-outline-secondary"
                >
                  Restart Configuration
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {infoFamily && (
        <FamilyInfoModal
          family={infoFamily}
          isAdsEmployee={isAdsEmployee}
          onClose={() => setInfoFamily(null)}
        />
      )}
    </div>
  );
}
